import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const Pagination = ({ 
  currentPage, 
  totalPages, 
  totalItems,
  itemsPerPage,
  itemsOnCurrentPage,
  hasNextPage,
  hasPrevPage,
  nextPage,
  prevPage,
  onPageChange, 
  baseUrl = '',
  queryParams = {}
}) => {
  // Mobile detection for responsive visible page numbers (< 1 2 3 4 >)
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 640
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Don't show pagination when there are no pages
  if (totalPages == null || totalPages <= 1) return null;

  const getPageUrl = (page) => {
    if (baseUrl) {
      const params = new URLSearchParams({ ...queryParams, page });
      return `${baseUrl}?${params.toString()}`;
    }
    return null;
  };

  const handlePageChange = (page) => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    } catch {
      window.scrollTo(0, 0);
    }
    if (onPageChange) {
      onPageChange(page);
    } else if (baseUrl) {
      window.location.href = getPageUrl(page);
    }
  };

  const renderPageButton = (page, label = null) => {
    const isActive = page === currentPage;
    const buttonClass = `min-w-[36px] h-9 sm:min-w-[40px] sm:h-10 px-2 flex items-center justify-center rounded-xl text-sm sm:text-base font-semibold transition-all active:scale-95 ${
      isActive
        ? 'bg-primary-600 text-white shadow-md shadow-primary-600/30 font-bold'
        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
    }`;

    if (baseUrl && !onPageChange) {
      return (
        <Link
          to={getPageUrl(page)}
          onClick={() => {
            try {
              window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            } catch {
              window.scrollTo(0, 0);
            }
          }}
          className={buttonClass}
          aria-current={isActive ? 'page' : undefined}
        >
          {label || page}
        </Link>
      );
    }

    return (
      <button
        onClick={() => handlePageChange(page)}
        className={buttonClass}
        disabled={isActive}
        aria-current={isActive ? 'page' : undefined}
      >
        {label || page}
      </button>
    );
  };

  // On mobile show 4 pages max (< 1 2 3 4 >), on desktop show 7
  const maxVisible = isMobile ? 4 : 7;
  let startPage, endPage;

  if (totalPages <= maxVisible) {
    startPage = 1;
    endPage = totalPages;
  } else {
    const halfVisible = Math.floor(maxVisible / 2);
    if (currentPage <= halfVisible) {
      startPage = 1;
      endPage = maxVisible;
    } else if (currentPage >= totalPages - halfVisible) {
      startPage = totalPages - maxVisible + 1;
      endPage = totalPages;
    } else {
      startPage = currentPage - halfVisible;
      endPage = startPage + maxVisible - 1;
    }
  }

  const pages = [];
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  const prevButtonClass = `h-9 sm:h-10 px-2.5 sm:px-3.5 flex items-center justify-center gap-1 rounded-xl text-sm font-semibold transition-all ${
    currentPage > 1
      ? 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm active:scale-95'
      : 'bg-gray-100 dark:bg-gray-800/40 text-gray-400 dark:text-gray-600 border border-gray-200 dark:border-gray-800 cursor-not-allowed opacity-50'
  }`;

  const nextButtonClass = `h-9 sm:h-10 px-2.5 sm:px-3.5 flex items-center justify-center gap-1 rounded-xl text-sm font-semibold transition-all ${
    currentPage < totalPages
      ? 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm active:scale-95'
      : 'bg-gray-100 dark:bg-gray-800/40 text-gray-400 dark:text-gray-600 border border-gray-200 dark:border-gray-800 cursor-not-allowed opacity-50'
  }`;

  return (
    <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8 mb-6 select-none">
      <div className="flex items-center justify-center gap-1 sm:gap-1.5 flex-wrap">
        {/* Previous Button (< on mobile, < Previous on desktop) */}
        {currentPage > 1 ? (
          baseUrl && !onPageChange ? (
            <Link
              to={getPageUrl(currentPage - 1)}
              onClick={() => {
                try {
                  window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                } catch {
                  window.scrollTo(0, 0);
                }
              }}
              className={prevButtonClass}
              title="Previous Page"
              aria-label="Previous Page"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Previous</span>
            </Link>
          ) : (
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              className={prevButtonClass}
              title="Previous Page"
              aria-label="Previous Page"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Previous</span>
            </button>
          )
        ) : (
          <span className={prevButtonClass} aria-disabled="true">
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Previous</span>
          </span>
        )}

        {/* First Page + Ellipsis on Desktop only */}
        {!isMobile && startPage > 1 && (
          <>
            {renderPageButton(1)}
            {startPage > 2 && (
              <span className="px-1 text-gray-400 dark:text-gray-500 font-semibold">...</span>
            )}
          </>
        )}

        {/* Page Numbers */}
        {pages.map((page) => (
          <React.Fragment key={page}>
            {renderPageButton(page)}
          </React.Fragment>
        ))}

        {/* Last Page + Ellipsis on Desktop only */}
        {!isMobile && endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && (
              <span className="px-1 text-gray-400 dark:text-gray-500 font-semibold">...</span>
            )}
            {renderPageButton(totalPages)}
          </>
        )}

        {/* Next Button (> on mobile, Next > on desktop) */}
        {currentPage < totalPages ? (
          baseUrl && !onPageChange ? (
            <Link
              to={getPageUrl(currentPage + 1)}
              onClick={() => {
                try {
                  window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                } catch {
                  window.scrollTo(0, 0);
                }
              }}
              className={nextButtonClass}
              title="Next Page"
              aria-label="Next Page"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="w-4 h-4" />
            </Link>
          ) : (
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              className={nextButtonClass}
              title="Next Page"
              aria-label="Next Page"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          )
        ) : (
          <span className={nextButtonClass} aria-disabled="true">
            <span className="hidden sm:inline">Next</span>
            <ChevronRight className="w-4 h-4" />
          </span>
        )}
      </div>

      {/* Page Info on Desktop */}
      {totalItems !== undefined && (
        <div className="text-xs text-gray-500 dark:text-gray-400 hidden lg:block sm:ml-3">
          Page {currentPage} of {totalPages} ({totalItems} items)
        </div>
      )}
    </div>
  );
};

export default Pagination;
